import { createFileRoute, Link } from "@tanstack/react-router";
import { useState } from "react";
import { ArrowLeft, ChevronRight, Wrench, BookOpen } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  getTroubleshootingNode,
  TROUBLESHOOTING_ROOT_ID,
} from "@/lib/product-knowledge/troubleshooting-tree";
import { docsDeepLink, resolveHelpArticleById } from "@/lib/product-knowledge/contextual-help";
import { renderArticleMarkdown } from "@/components/help/ArticleRenderer";
import { trackHelpEvent } from "@/lib/product-knowledge/help-analytics";
import { PRODUCT_DISPLAY_NAME, publicSitePath } from "@/lib/public-site";
import { useForceLightTheme } from "@/hooks/use-force-light-theme";

export const Route = createFileRoute("/docs_/troubleshooting")({
  component: TroubleshootingPage,
  head: () => ({
    meta: [
      { title: `Solución de problemas — ${PRODUCT_DISPLAY_NAME}` },
      { name: "description", content: "Diagnóstico guiado: pagos, emails, dominios, pedidos y más." },
      { name: "robots", content: "index, follow" },
    ],
    links: [{ rel: "canonical", href: publicSitePath("/docs/troubleshooting") }],
  }),
});

function TroubleshootingPage() {
  useForceLightTheme();
  const [stack, setStack] = useState<string[]>([TROUBLESHOOTING_ROOT_ID]);
  const currentId = stack[stack.length - 1] ?? TROUBLESHOOTING_ROOT_ID;
  const node = getTroubleshootingNode(currentId);

  const goTo = (id: string) => setStack((s) => [...s, id]);
  const goBack = () => setStack((s) => (s.length > 1 ? s.slice(0, -1) : s));

  if (!node) {
    return (
      <div className="mx-auto max-w-2xl px-4 py-16 text-center">
        <p className="text-muted-foreground">Nodo no encontrado.</p>
        <Link to="/docs/troubleshooting" search={{} as never} className="text-primary text-sm mt-4 inline-block">
          Reiniciar
        </Link>
      </div>
    );
  }

  const article = node.articleId ? resolveHelpArticleById(node.articleId) : undefined;

  return (
    <div className="min-h-screen bg-background text-foreground">
      <header className="border-b border-border/60 px-4 py-4">
        <div className="mx-auto flex max-w-2xl items-center justify-between">
          <Link to="/docs" search={{} as never} className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground">
            <ArrowLeft className="h-4 w-4" /> Documentación
          </Link>
          <span className="text-xs text-muted-foreground">{PRODUCT_DISPLAY_NAME}</span>
        </div>
      </header>

      <main className="mx-auto max-w-2xl px-4 py-10">
        <div className="mb-6 flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary/15 text-primary">
            <Wrench className="h-5 w-5" />
          </div>
          <div>
            <h1 className="text-xl font-bold">Solución de problemas</h1>
            <p className="text-sm text-muted-foreground">Diagnóstico guiado paso a paso</p>
          </div>
        </div>

        <div className="glass-card rounded-xl border border-border p-6">
          {stack.length > 1 && (
            <button type="button" onClick={goBack} className="mb-4 text-xs text-primary hover:underline">
              ← Anterior
            </button>
          )}

          <h2 className="text-lg font-semibold">{node.title}</h2>
          {node.prompt && <p className="mt-2 text-sm text-muted-foreground">{node.prompt}</p>}

          {node.options && (
            <div className="mt-4 space-y-2">
              {node.options.map((opt) => (
                <button
                  key={opt.nextId}
                  type="button"
                  onClick={() => {
                    trackHelpEvent({ type: "search", query: `ts:${opt.nextId}`, resultCount: 1 });
                    goTo(opt.nextId);
                  }}
                  className="flex w-full items-center justify-between rounded-lg border border-border px-4 py-3 text-left text-sm hover:bg-muted/50 transition-colors"
                >
                  {opt.label}
                  <ChevronRight className="h-4 w-4 text-muted-foreground" />
                </button>
              ))}
            </div>
          )}

          {node.resolution && (
            <div className="mt-4 rounded-lg border border-primary/20 bg-primary/5 p-4 text-sm leading-relaxed">
              {node.resolution}
            </div>
          )}

          {article && (
            <div className="mt-6 border-t border-border pt-6">
              <p className="mb-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">Guía relacionada</p>
              <h3 className="font-semibold">{article.title}</h3>
              <div className="mt-3">
                {renderArticleMarkdown(
                  article.content.slice(0, 800) + (article.content.length > 800 ? "\n\n…" : ""),
                )}
              </div>
              <Button variant="outline" size="sm" className="mt-4 gap-1.5" asChild>
                <Link
                  to={docsDeepLink(article.id) as never}
                  onClick={() => trackHelpEvent({ type: "article_view", articleId: article.id })}
                >
                  <BookOpen className="h-3.5 w-3.5" /> Ver guía completa
                </Link>
              </Button>
            </div>
          )}
        </div>

        <p className="mt-6 text-center text-xs text-muted-foreground">
          ¿No encontraste la respuesta?{" "}
          <a href="mailto:soporte@structura.com.ar" className="text-primary hover:underline">
            Contactá soporte
          </a>
        </p>
      </main>
    </div>
  );
}
